import { MenuItem, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface DateRangeMonthlyInputProps {
  label: string;
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  maxMonths?: number;
}

const MONTHS = [
  { value: '0', label: 'Enero' },
  { value: '1', label: 'Febrero' },
  { value: '2', label: 'Marzo' },
  { value: '3', label: 'Abril' },
  { value: '4', label: 'Mayo' },
  { value: '5', label: 'Junio' },
  { value: '6', label: 'Julio' },
  { value: '7', label: 'Agosto' },
  { value: '8', label: 'Septiembre' },
  { value: '9', label: 'Octubre' },
  { value: '10', label: 'Noviembre' },
  { value: '11', label: 'Diciembre' },
];

const YEARS = Array.from({ length: 10 }, (_, index) =>
  (new Date().getFullYear() - index).toString(),
);

/**
 * Componente para seleccionar un mes/año
 * Automáticamente genera startDate (día 1) y endDate (último día del mes)
 */
const DateRangeMonthlyInput: React.FC<DateRangeMonthlyInputProps> = ({
  label,
  startValue,
  endValue: _endValue,
  onStartChange,
  onEndChange,
  maxMonths: _maxMonths,
}) => {
  // Extraer mes y año del startValue
  const getMonthYearFromDate = (dateString: string) => {
    if (!dateString) return { month: '', year: '' };
    // Parsear directamente del string para evitar problemas de timezone
    // Formato esperado: YYYY-MM-DD
    const parts = dateString.split('-');
    if (parts.length < 2) return { month: '', year: '' };
    const year = parts[0];
    const month = (parseInt(parts[1]) - 1).toString(); // Restar 1 porque los meses van de 0-11
    return { month, year };
  };

  const initialMonthYear = getMonthYearFromDate(startValue);
  const [selectedMonth, setSelectedMonth] = useState(initialMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(initialMonthYear.year);

  // Sincronizar cuando cambia el valor externo
  useEffect(() => {
    const { month, year } = getMonthYearFromDate(startValue);
    setSelectedMonth(month);
    setSelectedYear(year);
  }, [startValue]);

  // Función para generar las fechas del mes
  const generateMonthDates = (month: string, year: string) => {
    if (!month || !year) {
      onStartChange('');
      onEndChange('');
      return;
    }

    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    // Primer día del mes
    const firstDay = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-01`;

    // Último día del mes
    const lastDay = new Date(yearNum, monthNum + 1, 0).getDate();
    const lastDayFormatted = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    onStartChange(firstDay);
    onEndChange(lastDayFormatted);
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    generateMonthDates(month, selectedYear);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    generateMonthDates(selectedMonth, year);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography variant="body2" marginBottom={1}>
        {label}
      </Typography>
      <div style={{ display: 'flex', gap: 8 }}>
        <TextField
          select
          label="Mes"
          size="small"
          value={selectedMonth}
          onChange={(e) => handleMonthChange(e.target.value)}
          fullWidth
        >
          {MONTHS.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Año"
          size="small"
          value={selectedYear}
          onChange={(e) => handleYearChange(e.target.value)}
          fullWidth
        >
          {YEARS.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </TextField>
      </div>
    </div>
  );
};

export default DateRangeMonthlyInput;
